import React, { useEffect, useState } from 'react';
import Modal from '../../common/Modal';
import Input from '../../common/Input';
import Select from '../../common/Select';
import Button from '../../common/Button';
import { validateRequired, validateEmail, validatePhone, validatePassword } from '../../../utils/validators';
import engineerService from '../../../services/engineerService';
import managerService from '../../../services/managerService';
import { ROLES } from '../../../constants/roles';

const AddEngineerModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contact: '',
    password: '',
    team_lead_id: '',
    role: ROLES.ENGINEER
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [teamLeads, setTeamLeads] = useState([]);
  const [loadingTLs, setLoadingTLs] = useState(false);
  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadTeamLeads();
      loadManagers();
    }
  }, [isOpen]);

  const loadManagers = async () => {
    setLoadingManagers(true);
    const result = await managerService.getAllManagers();
    if (result.success) setManagers(result.data);
    setLoadingManagers(false);
  };

  const loadTeamLeads = async () => {
    setLoadingTLs(true);
    const result = await engineerService.getAllTeamLeads();
    if (result.success) {
      setTeamLeads(result.data);
    }
    setLoadingTLs(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!validateRequired(formData.name)) {
      newErrors.name = 'Name is required';
    }
    if (!validateRequired(formData.email)) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!validateRequired(formData.contact)) {
      newErrors.contact = 'Contact number is required';
    } else if (!validatePhone(formData.contact)) {
      newErrors.contact = 'Please enter a valid 10-digit phone number';
    }
    if (!validateRequired(formData.password)) {
      newErrors.password = 'Password is required';
    } else if (!validatePassword(formData.password)) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;

    setSubmitting(true);

    try {
      const engineerData = {
        ...formData,
        team_lead_id: formData.team_lead_id ? parseInt(formData.team_lead_id, 10) : null
      };
      
      await onSubmit(engineerData);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        contact: '',
        password: '',
        team_lead_id: '',
        role: ROLES.ENGINEER
      });
      setErrors({});
      onClose();
    } catch (error) {
      console.error('Error creating engineer:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setFormData({
        name: '',
        email: '',
        contact: '',
        designation: '',
        password: '',
        team_lead_id: '',
        role: ROLES.ENGINEER
      });
      setErrors({});
      onClose();
    }
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
    marginBottom: '16px'
  };

  const teamLeadOptions = [
    { value: '', label: '-- No Supervisor --' },
    ...teamLeads.map(tl => ({
      value: tl.id.toString(),
      label: `${tl.name} (TL)`
    })),
    ...managers.map(m => ({
      value: m.id.toString(),
      label: `${m.name} (Manager)`
    }))
  ];

  const footer = (
    <>
      <Button variant="secondary" onClick={handleClose} disabled={submitting}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
        {submitting ? 'Creating...' : 'Create Engineer'}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add New Engineer"
      size="large"
      footer={footer}
    >
      <form onSubmit={handleSubmit}>
        <div style={gridStyle}>
          <Input
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={errors.name}
            required
          />
          <Input
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
            required
          />
        </div>

        <div style={gridStyle}>
          <Input
            label="Contact"
            name="contact"
            value={formData.contact}
            onChange={handleChange}
            error={errors.contact}
            required
          />
          <Input
            label="Designation"
            name="designation"
            placeholder="e.g., Field Engineer"
            value={formData.designation}
            onChange={handleChange}
            error={errors.designation}
          />
          <Input
            label="Password"
            name="password"
            type="password"
            canPeekPassword
            value={formData.password}
            onChange={handleChange}
            error={errors.password}
            required
          />
        </div>

        <div style={gridStyle}>
          <Select
            label="Team Leader"
            name="team_lead_id"
            value={formData.team_lead_id}
            onChange={handleChange}
            options={teamLeadOptions}
            error={errors.team_lead_id}
            disabled={loadingTLs}
          />
        </div>
      </form>
    </Modal>
  );
};

export default AddEngineerModal;